import { assign } from 'xstate';

import { PackageType, PlansVariant, ProviderPlanId } from '__generated__/GQL';
import assertEventType from 'common/bento/lib/assertEventType';
import { MachineActions } from 'common/bento/types/MachineConfig';
import { logEvent } from 'features/Analytics/analytics';

import { Model } from './model';

export enum Action {
  AssignSelectedPlan = 'assignSelectedPlan',
  AssignSelectedPackage = 'assignSelectedPackage',
  LogAndAssignCoupon = 'logAndAssignCoupon',
  ResetCoupon = 'resetCoupon',
  AssignVariant = 'assignVariant',
  AssignDefaultVariant = 'assignDefaultVariant',
  ResetVariant = 'resetVariant',
}

const actions: MachineActions<Model, Action> = {
  assignDefaultVariant: assign({
    selectedVariant: ({ getModuleData }) => {
      const { selectedPlanId } = getModuleData().viewer.company.metadata;

      if (
        selectedPlanId === ProviderPlanId.Pro ||
        selectedPlanId === ProviderPlanId.Business
      ) {
        return PlansVariant.Team;
      }

      if (
        selectedPlanId === ProviderPlanId.Basic ||
        selectedPlanId === ProviderPlanId.Plus
      ) {
        return PlansVariant.Solo;
      }

      return null;
    },
  }),

  assignSelectedPackage: assign((context, event) => {
    assertEventType(event, 'SAVE_SELECTED_PACKAGE');

    return {
      selectedPackage: event.data,
      selectedPlan: event.data.plan,
    };
  }),

  assignSelectedPlan: assign({
    selectedPlan: (context, event) => {
      assertEventType(event, 'SAVE_SELECTED_PLAN');

      return event.data;
    },
  }),

  assignVariant: assign((context, event) => {
    assertEventType(event, 'SAVE_VARIANT');

    return {
      selectedVariant: event.data,
    };
  }),

  logAndAssignCoupon: assign((context, event) => {
    assertEventType(event, 'SUBMIT_COUPON');

    const { packageType } = context.getModuleData();

    if (packageType === null) {
      throw Error('Cannot use coupon without packageType');
    }

    const packageTypeToEventName = {
      [PackageType.CapitalDeposit]: 'Capital_Deposit_Coupon_Applied',
      [PackageType.ShineStart]: 'Shine_Start_Coupon_Applied',
    };

    const eventName = packageTypeToEventName[packageType];

    logEvent({
      name: eventName,
      properties: { couponId: event.data },
    });

    return {
      selectedCoupon: event.data,
    };
  }),

  // Required to avoid TS error
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  resetCoupon: assign((context) => ({
    selectedCoupon: null,
  })),

  // Required to avoid TS error
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  resetVariant: assign((context) => {
    logEvent({
      name: 'Price Plan Variant Reset',
      properties: {
        from: context.selectedVariant?.toLowerCase(),
      },
    });
    return {
      selectedVariant: null,
    };
  }),
};

export default actions;
